import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — Documentos (Formatos PDF), `/api/documents`. Módulo 16.
 *
 * El módulo está **en retirada** desde el 2026-08-27 —lo sustituye
 * `solicitudes.py`— pero sigue montado porque lo ya radicado es evidencia. Se
 * migra con el mismo criterio que los quince anteriores: lo que está mal no se
 * queda, aunque la pantalla tenga fecha de caducidad.
 *
 * ## Lo que este archivo arregla
 *
 * `recursoConSesion()`. La configuración de campos pinta la página del PDF con
 * `<img src="/api/documents/templates/N/page/0">`, y esa ruta exige rol de
 * gestión: **una etiqueta `<img>` no manda la cabecera `Authorization`**, así
 * que el servidor responde 401 y la imagen nunca carga. Comprobado con una
 * petición real el 2026-09-24. Aquí la imagen se pide con el token y se pinta
 * desde un blob.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

export type EstadoDeSolicitud =
  | 'enviada' | 'en_revision' | 'aprobada' | 'rechazada' | 'respondida';

export const ESTADOS: Array<[EstadoDeSolicitud, string]> = [
  ['enviada', 'Enviada'],
  ['en_revision', 'En revisión'],
  ['aprobada', 'Aprobada'],
  ['rechazada', 'Rechazada'],
  ['respondida', 'Respondida'],
];

/** Las ocho claves que el servidor sabe resolver solo (`_auto_value`). */
export const AUTOMATICOS: Array<[string, string]> = [
  ['nombre', 'Nombre'], ['cedula', 'Cédula'], ['cargo', 'Cargo'], ['area', 'Área'],
  ['email', 'Correo'], ['ciudad', 'Ciudad'], ['fecha', 'Fecha'], ['empresa', 'Empresa'],
];

export interface CampoDePlantilla {
  name: string;
  label: string;
  type: 'text' | 'date' | 'textarea';
  source: 'manual' | 'auto';
  auto_key?: string | null;
  page: number;
  x: number;
  y: number;
  size: number;
  required?: boolean;
}

export interface CampoManual {
  name: string;
  label: string;
  type: string;
  required?: boolean;
}

export interface Plantilla {
  id: number;
  name: string;
  description?: string | null;
  icon: string;
  color: string;
  status: 'borrador' | 'publicado' | 'archivado';
  has_pdf: boolean;
  page_count: number;
  field_count: number;
  manual_fields: CampoManual[];
  /** Solo para gestión. */
  fields?: CampoDePlantilla[];
  recipient_emails?: string | null;
}

export interface Solicitud {
  id: number;
  radicado: string;
  template_id: number;
  template_name: string;
  icon: string;
  status: EstadoDeSolicitud;
  status_label: string;
  created_at?: string | null;
  has_response: boolean;
  response_note?: string | null;
  /** Solo para gestión. */
  user_name?: string;
  area?: string;
}

export interface Evento {
  action: string;
  note?: string | null;
  actor: string;
  created_at?: string | null;
}

export interface DetalleDeSolicitud extends Solicitud {
  pdf_url?: string | null;
  response_url?: string | null;
  timeline: Evento[];
}

export interface TamanosDePagina {
  sizes: Array<{ w: number; h: number }>;
  zoom: number;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  todo: ['documentos'] as const,
  plantillas: ['documentos', 'plantillas'] as const,
  plantilla: (id: number) => ['documentos', 'plantilla', id] as const,
  tamanos: (id: number) => ['documentos', 'tamanos', id] as const,
  pagina: (id: number, n: number) => ['documentos', 'pagina', id, n] as const,
  solicitudes: ['documentos', 'solicitudes'] as const,
  mias: ['documentos', 'mias'] as const,
  resumen: ['documentos', 'resumen'] as const,
  solicitud: (id: number) => ['documentos', 'solicitud', id] as const,
};

function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: claves.todo });
}

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function usePlantillas(habilitado = true) {
  return useQuery({
    queryKey: claves.plantillas,
    queryFn: () => api.get<Plantilla[]>('/documents/templates'),
    enabled: habilitado,
  });
}

export function usePlantilla(id: number | null) {
  return useQuery({
    queryKey: claves.plantilla(id ?? 0),
    queryFn: () => api.get<Plantilla>(`/documents/templates/${id}`),
    enabled: id != null,
  });
}

export function useTamanosDePagina(id: number | null, habilitado: boolean) {
  return useQuery({
    queryKey: claves.tamanos(id ?? 0),
    queryFn: () => api.get<TamanosDePagina>(`/documents/templates/${id}/page-sizes`),
    enabled: id != null && habilitado,
  });
}

/**
 * La imagen de una página, con la sesión puesta.
 *
 * Es una consulta y no un `<img src>` porque la ruta exige rol de gestión: el
 * navegador no manda la cabecera en una etiqueta `<img>`. El blob se libera al
 * cambiar de página, que es lo que `URL.createObjectURL` obliga a recordar.
 */
export function usePaginaDePlantilla(id: number | null, pagina: number, habilitado: boolean) {
  return useQuery({
    queryKey: claves.pagina(id ?? 0, pagina),
    queryFn: () => recursoConSesion(`/api/documents/templates/${id}/page/${pagina}`),
    enabled: id != null && habilitado,
    // La página de un PDF no cambia mientras no se suba otro.
    staleTime: 10 * 60_000,
    gcTime: 10 * 60_000,
  });
}

export function useSolicitudes(habilitado: boolean) {
  return useQuery({
    queryKey: claves.solicitudes,
    queryFn: () => api.get<Solicitud[]>('/documents/requests'),
    enabled: habilitado,
  });
}

export function useMisSolicitudes(habilitado: boolean) {
  return useQuery({
    queryKey: claves.mias,
    queryFn: () => api.get<Solicitud[]>('/documents/requests/mine'),
    enabled: habilitado,
  });
}

export function useResumen(habilitado: boolean) {
  return useQuery({
    queryKey: claves.resumen,
    queryFn: () => api.get<{ counts: Record<string, number>; total: number }>(
      '/documents/requests/summary'),
    enabled: habilitado,
  });
}

export function useSolicitud(id: number | null) {
  return useQuery({
    queryKey: claves.solicitud(id ?? 0),
    queryFn: () => api.get<DetalleDeSolicitud>(`/documents/requests/${id}`),
    enabled: id != null,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

export interface CuerpoDePlantilla {
  name: string;
  description: string | null;
  recipient_emails?: string | null;
  icon?: string;
  color?: string;
  status?: string;
}

export function useGuardarPlantilla() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: CuerpoDePlantilla }) =>
      // El POST devuelve el `id` de la plantilla nueva y el PUT no: quien llama
      // lo usa para abrir la configuración, que es lo siguiente que hace falta.
      v.id
        ? api.put<{ id?: number; message: string }>(`/documents/templates/${v.id}`, v.cuerpo)
        : api.post<{ id?: number; message?: string }>('/documents/templates', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarPlantilla() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/documents/templates/${id}`),
    onSuccess: () => refrescar(),
  });
}

export interface CampoSugerido {
  name?: string;
  page?: number;
  x: number;
  y: number;
  size?: number;
}

export function useSubirPdf() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; archivo: File }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      return api.upload<{ message: string; suggested_fields?: CampoSugerido[] }>(
        `/documents/templates/${v.id}/pdf`, datos);
    },
    onSuccess: () => refrescar(),
  });
}

export function useGuardarCampos() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; campos: CampoDePlantilla[] }) =>
      api.put<{ message: string; count: number }>(
        `/documents/templates/${v.id}/fields`, { fields: v.campos }),
    onSuccess: () => refrescar(),
  });
}

export function useRadicar() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { template_id: number; data: Record<string, string> }) =>
      api.post<{ id: number; radicado: string; message: string }>('/documents/requests', v),
    onSuccess: () => refrescar(),
  });
}

export function useCambiarEstado() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; status: EstadoDeSolicitud; note: string | null }) =>
      api.post<{ message: string }>(`/documents/requests/${v.id}/status`,
        { status: v.status, note: v.note }),
    onSuccess: () => refrescar(),
  });
}

export function useAdjuntarRespuesta() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; archivo: File; nota: string }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      datos.append('note', v.nota);
      // `API.upload` lleva el token; el vanilla armaba aquí un `fetch` a mano.
      return api.upload<{ message: string }>(`/documents/requests/${v.id}/response`, datos);
    },
    onSuccess: () => refrescar(),
  });
}

/* ── Binarios con la sesión puesta ─────────────────────────────────────── */

/**
 * Un recurso binario de la API, como URL de objeto.
 *
 * El token sale de donde lo deja `api.js` y lo lee `Utils.downloadAuthed`: no
 * hay una segunda sesión, solo un segundo consumidor. Quien llama es
 * responsable de revocar la URL —`URL.revokeObjectURL`— cuando deja de
 * pintarla; aquí lo hace React Query al expulsar la entrada de caché.
 */
export async function recursoConSesion(url: string): Promise<string> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
  });
  if (!res.ok) {
    const err: Error & { status?: number } = new Error(
      res.status === 401 || res.status === 403
        ? 'No tienes acceso a este documento.'
        : 'No se pudo cargar el documento.');
    err.status = res.status;
    throw err;
  }
  return URL.createObjectURL(await res.blob());
}

/** La previsualización del PDF con datos de ejemplo, en otra pestaña.
 *
 *  El vanilla hacía `window.open('/api/documents/templates/N/preview')`, que
 *  abre una pestaña **sin la cabecera de sesión**: el servidor respondía 401 y
 *  lo que se veía era el JSON del error. */
export async function abrirPrevisualizacion(id: number): Promise<void> {
  const url = await recursoConSesion(`/api/documents/templates/${id}/preview`);
  window.open(url, '_blank', 'noopener');
  // Un minuto es de sobra para que la pestaña cargue el blob, y evita retener
  // el PDF entero en memoria mientras dure la sesión.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
